import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  InputAdornment,
  CircularProgress,
  Fade,
  Divider,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  Drawer
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ErrorOutline as ErrorIcon
} from "@mui/icons-material";
import HistoryPanel from './components/HistoryPanel';
import VoiceInputButton from './components/VoiceInputButton';
import VoiceSpeakerButton from './components/VoiceSpeakerButton';
import { cleanupTTSManager } from './services/VoiceTTSManager';
import { 
  getTimeAgo, 
  stripFileExtension, 
  buildUniqueSources, 
  extractSourceCodeFromText, 
  prettyPrintJsonIfPossible, 
  normalizeForDisplay,
  getHighlightedTextParts,
  exportChatHistory,
  downloadTextAsFile,
  getPrevUserQuestion
} from './utils/chatUtils';
import { 
  sendChatMessage, 
  fetchConversations, 
  createNewConversation, 
  fetchChatHistory, 
  renameConversation, 
  deleteConversation, 
  fetchQuestionSuggestions,
  generateBusinessReport
} from './utils/apiUtils';
import './styles/ChatWidget.css';

const DepartmentTaskChatbot = () => {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [sourceDrawerOpen, setSourceDrawerOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('chatSessionId') || null);
  const [conversations, setConversations] = useState([]);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [historyPanelVisible, setHistoryPanelVisible] = useState(true);
  const [questionSuggestions, setQuestionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false); // Hidden by default
  const [reportData, setReportData] = useState({
    organizationId: 9,
    sessionId: "session_456",
    reportType: "project",
    reportTitle: "",
    customQueries: []
  });
  const [newQuery, setNewQuery] = useState("");
  
  const messagesEndRef = useRef(null);

  // Voice input handlers
  const handleVoiceTranscript = (transcript) => {
    if (transcript.trim()) {
      setInput(transcript);
      // Auto-focus the input field after voice input
      setTimeout(() => {
        const inputElement = document.querySelector('textarea[placeholder="Type your message here..."]');
        if (inputElement) {
          inputElement.focus();
          inputElement.setSelectionRange(transcript.length, transcript.length);
        }
      }, 100);
    }
  };

  const handleVoiceInterim = (interimText) => {
    // Real-time typing effect - show interim results
    if (interimText.trim()) {
      setInput(interimText);
    }
  };

  const handleVoiceError = (error) => {
    console.error('Voice input error:', error);
    setNotification({
      open: true,
      message: error.userMessage || 'Voice input failed. Please try again.',
      severity: 'error'
    });
  };

  // TTS handlers
  const handleTTSError = (error) => {
    console.error('Voice output error:', error);
    setNotification({
      open: true,
      message: error.message || 'Voice output failed. Please try again.',
      severity: 'error'
    });
  };

  // Initialize user from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    // Add a small delay to ensure DOM is updated
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Fetch question suggestions from the current chat session
  const fetchQuestionSuggestionsCallback = useCallback(async () => {
    if (!sessionId) return;
    
    const suggestions = await fetchQuestionSuggestions(sessionId, 5);
    setQuestionSuggestions(suggestions);
    // Don't auto-show suggestions - keep them hidden until user clicks
    // setShowSuggestions(suggestions.length > 0);
  }, [sessionId]);

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion.question);
    setShowSuggestions(false);
  };

  // Fetch conversations list
  useEffect(() => {
    const loadConversations = async () => {
      try {
        const list = await fetchConversations();
        setConversations(Array.isArray(list) ? list : []);
      } catch (e) {
        setConversations([]);
      }
    };
    loadConversations();
  }, []);

     // Load messages for current sessionId on mount
   useEffect(() => {
     const loadHistory = async () => {
       if (!sessionId) return;
       
       // Don't load history if we already have messages (prevents overwriting current conversation)
       if (messages.length > 0) return;
       
       try {
         const hist = await fetchChatHistory(sessionId);
         const mapped = (hist || []).map(m => ({ 
           role: (m.type || '').toLowerCase() === 'user' ? 'user' : 'assistant', 
           content: m.content, 
           sources: m.sources || [] 
         }));
         setMessages(mapped);
       } catch (e) {
         setMessages([]);
       }
     };
     
     loadHistory();
   }, [sessionId, messages.length]);

  // Fetch question suggestions when session changes
  useEffect(() => {
    if (sessionId) {
      fetchQuestionSuggestionsCallback();
    } else {
      setQuestionSuggestions([]);
      setShowSuggestions(false);
    }
  }, [sessionId, fetchQuestionSuggestionsCallback]);

  // Auto-hide suggestions when user starts typing
  useEffect(() => {
    if (input.trim()) {
      setShowSuggestions(false);
    }
  }, [input]);

  // Cleanup TTS when component unmounts
  useEffect(() => {
    return () => {
      cleanupTTSManager();
    };
  }, []);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setInput("");
    setLoading(true);
    setShowSuggestions(false);
    
    // Prevent page scrolling by maintaining scroll position
    const currentScrollY = window.scrollY;

    try {
      let currentSessionId = sessionId;
      console.log('Current sessionId:', currentSessionId);
      
      if (!currentSessionId) {
        console.log('No sessionId, creating new conversation...');
        console.log('User object:', user);
        console.log('Organization ID:', user?.organization?.id);
        
        if (!user?.organization?.id) {
          throw new Error('User organization not found. Please log in again.');
        }
        
        const newConversation = await createNewConversation(user.organization.id);
        console.log('New conversation created:', newConversation);
        currentSessionId = newConversation.sessionId;
        
        // Store in localStorage but don't update state yet (to prevent history loading)
        localStorage.setItem('chatSessionId', currentSessionId);
        
        const updatedConversations = await fetchConversations();
        setConversations(Array.isArray(updatedConversations) ? updatedConversations : []);
      }

      console.log('Sending message with sessionId:', currentSessionId);
      console.log('User department info:', { departmentCode: user.department?.code, departmentName: user.department?.name });
      const response = await sendChatMessage(userMessage, currentSessionId, user.organization.id, user.department?.code);
      console.log('Message response:', response);
      
             console.log('Bot response:', response);
       
       // Handle case where no knowledge base content is available
       let botContent = response.response || 'No response received';
       let botSources = response.sources || [];
       
       // If the response indicates no knowledge base, provide a helpful message
       if (botContent.includes("I don't have knowledge base content") || botContent.includes("no knowledge base content")) {
         botContent = "I don't have access to any documents in your knowledge base yet. Please upload some documents first, and then I'll be able to help you with questions about them.";
         botSources = []; // No sources for this message
       }
       
       setMessages(prev => [...prev, { 
         role: 'assistant', 
         content: botContent,
         sources: botSources
       }]);

      // Now update the sessionId state after messages are set
      if (!sessionId && currentSessionId) {
        setSessionId(currentSessionId);
      }

             await fetchQuestionSuggestionsCallback();
       
       // Restore scroll position to prevent page jumping
       window.scrollTo(0, currentScrollY);
       
     } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        sources: []
      }]);
         } finally {
       setLoading(false);
       
       // Ensure scroll position is maintained
       window.scrollTo(0, currentScrollY);
     }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = async () => {
    try {
      if (!user?.organization?.id) {
        throw new Error('User organization not found. Please log in again.');
      }
      
      const newConversation = await createNewConversation(user.organization.id);
      setSessionId(newConversation.sessionId);
      localStorage.setItem('chatSessionId', newConversation.sessionId);
      setMessages([]);
      setQuestionSuggestions([]);
      setShowSuggestions(false);
      
      const updatedConversations = await fetchConversations();
      setConversations(Array.isArray(updatedConversations) ? updatedConversations : []);
      setMobileHistoryOpen(false);
    } catch (error) {
      console.error('Error creating new conversation:', error);
    }
  };

  const loadConversation = async (convSessionId) => {
    try {
      setSessionId(convSessionId);
      localStorage.setItem('chatSessionId', convSessionId);
      
      const hist = await fetchChatHistory(convSessionId);
      const mapped = (hist || []).map(m => ({ 
        role: (m.type || '').toLowerCase() === 'user' ? 'user' : 'assistant', 
        content: m.content, 
        sources: m.sources || [] 
      }));
      setMessages(mapped);
      setMobileHistoryOpen(false);
      
      await fetchQuestionSuggestionsCallback();
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleRename = async (convSessionId, newTitle) => {
    try {
      await renameConversation(convSessionId, newTitle);
      const updatedConversations = await fetchConversations();
      setConversations(Array.isArray(updatedConversations) ? updatedConversations : []);
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }
  };

  const handleDeleteConversation = async (convSessionId) => {
    try {
      await deleteConversation(convSessionId);
      const updatedConversations = await fetchConversations();
      setConversations(Array.isArray(updatedConversations) ? updatedConversations : []);
      
      if (convSessionId === sessionId) {
        setSessionId(null);
        localStorage.removeItem('chatSessionId');
        setMessages([]);
        setQuestionSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // Handle source click
  const handleSourceClick = (source) => {
    setSelectedSource(source);
    setSourceDrawerOpen(true);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    // Only scroll to bottom when there are messages
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Render highlighted text
  const renderHighlighted = (text, queryText) => {
    if (!queryText || typeof text !== 'string') {
      return <span className="text-base">{text}</span>;
    }
    const tokens = (queryText.toLowerCase().match(/[a-z0-9]{3,}/g) || [])
      .filter((v, i, a) => a.indexOf(v) === i)
      .slice(0, 10);
    if (tokens.length === 0) {
      return <span className="text-base">{text}</span>;
    }
    const pattern = new RegExp(`(${tokens.map(t => t.replace(/[.*+?^${}()|[\\]\\\\]/g, r => "\\"+r)).join('|')})`, 'ig');
    const parts = text.split(pattern);
    return (
      <span className="text-base">
        {parts.map((part, idx) => (
          pattern.test(part) ? <mark key={idx} className="bg-yellow-200 px-1 rounded">{part}</mark> : <span key={idx}>{part}</span>
        ))}
      </span>
    );
  };

  // Show loading state if no user data
  if (!user) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span className="loading-text">Loading...</span>
      </div>
    );
  }

  return (
    <>
             {/* Main Chat Container */}
       <div className="chat-main-layout">
         {/* History Panel - positioned below header */}
         <HistoryPanel
           conversations={conversations}
           sessionId={sessionId}
           startNewChat={startNewChat}
           loadConversation={loadConversation}
           onRename={handleRename}
           onDelete={handleDeleteConversation}
           getTimeAgo={getTimeAgo}
           mobileOpen={mobileHistoryOpen}
           onMobileClose={() => setMobileHistoryOpen(false)}
           visible={historyPanelVisible}
         />
         
         <div className={`chat-content-area ${!historyPanelVisible ? 'history-hidden' : ''}`}>
           <div className="chat-container">
            
            {/* Header */}
            <div className="chat-header">
              <div className="chat-header-left">
                {/* Mobile menu button */}
                <button
                  className="mobile-menu-btn"
                  onClick={() => setMobileHistoryOpen(true)}
                  aria-label="Open chat history"
                >
                  <MenuIcon />
                </button>
                
                {/* Desktop history toggle button */}
                <button
                  className="desktop-history-toggle"
                  onClick={() => setHistoryPanelVisible(prev => !prev)}
                  title={historyPanelVisible ? "Hide History" : "Show History"}
                  aria-label={historyPanelVisible ? "Hide history" : "Show history"}
                >
                  {historyPanelVisible ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </button>
                
                <div className="chat-header-avatar">
                  <BotIcon />
                </div>
                <div>
                  <h1 className="chat-header-title">AI Assistant</h1>
                  <p className="chat-header-subtitle">Ready to help with your questions</p>
                </div>
              </div>
              
              <div className="chat-header-right">
                <div className="chat-user-info">
                  <p className="chat-user-email">{user.email}</p>
                  <p className="chat-user-department">{user.department?.name}</p>
                </div>
                
                {/* Three-dots menu for report and export options */}
                <Tooltip title="More options">
                  <IconButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
                
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={() => {
                    setAnchorEl(null);
                    setReportDialogOpen(true);
                  }}>
                    <BusinessIcon sx={{ mr: 1 }} />
                    Generate Report
                  </MenuItem>
                  <MenuItem onClick={() => {
                    setAnchorEl(null);
                    const chatText = exportChatHistory(messages);
                    downloadTextAsFile(chatText, `chat-export-${new Date().toISOString().split('T')[0]}.txt`);
                    setNotification({
                      open: true,
                      message: "Chat history exported successfully!",
                      severity: "success"
                    });
                  }}>
                    <DownloadIcon sx={{ mr: 1 }} />
                    Export Chat
                  </MenuItem>
                </Menu>
                
                <div className="user-avatar">
                  {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </div>
              </div>
            </div>

            {/* Messages Container */}
            <div className="messages-container">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <BotIcon />
                  </div>
                  <h2 className="empty-state-title">Welcome to AI Assistant</h2>
                  <p className="empty-state-subtitle">
                    Ask me anything about your documents or start a conversation
                  </p>
                </div>
              ) : (
                <>
                                     {messages.map((message, index) => (
                     <div key={index} className={`message-row ${message.role}`}>
                       {message.role === 'assistant' && (
                         <div className="message-avatar bot">
                           <BotIcon />
                         </div>
                       )}
                       
                       <div className={`message-bubble ${message.role}`}>
                         {message.content}
                         
                         {/* Speaker button for assistant messages */}
                         {message.role === 'assistant' && (
                           <VoiceSpeakerButton
                             messageId={`message-${index}`}
                             text={message.content}
                             onError={handleTTSError}
                             size="small"
                           />
                         )}
                       </div>
                       
                       {message.role === 'user' && (
                         <div className="message-avatar user">
                           {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                         </div>
                       )}
                       
                       {/* Sources for each assistant message */}
                       {message.role === 'assistant' && message.sources?.length > 0 && (
                         <div className="source-chips">
                           {buildUniqueSources(message.sources).map((source, i) => (
                             <button
                               key={`${index}-${i}`}
                               className="source-chip"
                               onClick={() => handleSourceClick(source)}
                             >
                               {stripFileExtension(source.filename || source.documentName || source.name)}
                             </button>
                           ))}
                         </div>
                       )}
                     </div>
                   ))}
                  
                  {/* Typing Indicator */}
                  {loading && (
                    <div className="message-row assistant">
                      <div className="message-avatar bot">
                        <BotIcon />
                      </div>
                      <div className="message-bubble assistant">
                        <div className="typing-container">
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                          <span className="typing-text">AI is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="input-area">
                             {/* Suggestions Toggle */}
               {questionSuggestions.length > 0 && (
                 <div className="suggestions-toggle">
                   <span className="suggestions-toggle-text">
                     {showSuggestions ? 'Hide suggestions' : 'Show suggestions'}
                   </span>
                   <button
                     className="suggestions-toggle-btn"
                     onClick={(event) => {
                       setShowSuggestions(prev => !prev);
                       // Prevent any scrolling when toggling suggestions
                       event.preventDefault();
                     }}
                   >
                     {showSuggestions ? 'Hide' : 'Show'}
                   </button>
                 </div>
               )}

              {/* Chat Input */}
              <div className="chat-input">
                               <TextField
                 fullWidth
                 multiline
                 maxRows={4}
                 variant="outlined"
                 placeholder="Type your message here..."
                 value={input}
                 onChange={(e) => {
                   setInput(e.target.value);
                   // Prevent any auto-scrolling when typing
                   e.preventDefault();
                 }}
                 onKeyPress={handleKeyPress}
                 disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <VoiceInputButton
                          onTranscript={handleVoiceTranscript}
                          onInterim={handleVoiceInterim}
                          onError={handleVoiceError}
                          disabled={loading}
                          size="small"
                        />
                        <button
                          type="button"
                          className={`send-button ${!input.trim() || loading ? 'disabled' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            sendMessage();
                          }}
                          disabled={!input.trim() || loading}
                        >
                          {loading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SendIcon />
                          )}
                        </button>
                      </InputAdornment>
                    )
                  }}
                />
              </div>

              {/* Suggestions */}
              {showSuggestions && questionSuggestions.length > 0 && (
                <div className="suggestions-container">
                  <h3 className="suggestions-title">Suggested questions:</h3>
                  <div className="suggestions-list">
                    {questionSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <div className="suggestion-question">{suggestion.question}</div>
                        <div className="suggestion-time">{getTimeAgo(suggestion.lastAskedAt)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

             {/* Source Drawer */}
       <Drawer
         anchor="right"
         open={sourceDrawerOpen}
         onClose={() => setSourceDrawerOpen(false)}
         sx={{
           '& .MuiDrawer-paper': {
             width: { xs: '100%', sm: 400 },
             padding: 2,
             top: '80px', // Start below header
             height: 'calc(100vh - 80px)' // Adjust height to not cover header
           }
         }}
       >
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
           <Typography variant="h6" sx={{ fontWeight: 600 }}>
             Source: {selectedSource ? stripFileExtension(selectedSource.filename || selectedSource.documentName || selectedSource.name || 'Unknown') : ''}
           </Typography>
           <IconButton onClick={() => setSourceDrawerOpen(false)}>
             <CloseIcon />
           </IconButton>
         </Box>
        
        <Divider sx={{ mb: 2 }} />
        
                 <Box sx={{ flex: 1, overflow: 'auto' }}>
           {selectedSource && (() => {
             // Use sourceText field which contains the actual document content/excerpt
             const content = selectedSource.sourceText;
             
             if (content) {
               return (
                 <div>
                   <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--color-bg-tertiary)', borderRadius: '8px' }}>
                     <strong>Document Excerpt:</strong>
                   </div>
                   <div style={{ 
                     whiteSpace: 'pre-wrap', 
                     fontSize: '16px', 
                     lineHeight: '1.7',
                     backgroundColor: 'white',
                     padding: '20px',
                     borderRadius: '8px',
                     border: '1px solid var(--color-border-medium)'
                   }}>
                     {content}
                   </div>
                 </div>
               );
             }
             
             // Fallback if sourceText is not available
             return (
               <div>
                 <p><strong>Source content not available</strong></p>
                 <p>Available fields: {Object.keys(selectedSource).join(', ')}</p>
                 <p>Filename: {selectedSource.filename || selectedSource.documentName || selectedSource.name || 'N/A'}</p>
                 <p>Similarity: {selectedSource.similarity || 'N/A'}</p>
                 <p>Source ID: {selectedSource.id || selectedSource.sourceId || 'N/A'}</p>
                 <details>
                   <summary>Raw source data</summary>
                   <pre style={{ fontSize: 10, overflow: 'auto' }}>
                     {JSON.stringify(selectedSource, null, 2)}
                   </pre>
                 </details>
               </div>
             );
           })()}
         </Box>
      </Drawer>

      {/* Report Generation Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1 }} />
            Generate Business Report
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportData.reportType}
                onChange={(e) => setReportData(prev => ({ ...prev, reportType: e.target.value }))}
                label="Report Type"
              >
                <MenuItem value="performance">Performance Report</MenuItem>
                <MenuItem value="financial">Financial Report</MenuItem>
                <MenuItem value="operational">Operational Report</MenuItem>
                <MenuItem value="strategic">Strategic Report</MenuItem>
                <MenuItem value="compliance">Compliance Report</MenuItem>
                <MenuItem value="project">Project Report</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Report Title"
              value={reportData.reportTitle}
              onChange={(e) => setReportData(prev => ({ ...prev, reportTitle: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="e.g., Q4 Performance Analysis"
            />
            
            <Typography variant="h6" sx={{ mb: 1 }}>Custom Questions (Optional)</Typography>
            <Box sx={{ mb: 2 }}>
              {reportData.customQueries.map((query, index) => (
                <Chip
                  key={index}
                  label={query}
                  onDelete={() => {
                    const newQueries = reportData.customQueries.filter((_, i) => i !== index);
                    setReportData(prev => ({ ...prev, customQueries: newQueries }));
                  }}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Add custom question"
                value={newQuery}
                onChange={(e) => setNewQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newQuery.trim()) {
                    setReportData(prev => ({ 
                      ...prev, 
                      customQueries: [...prev.customQueries, newQuery.trim()] 
                    }));
                    setNewQuery('');
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  if (newQuery.trim()) {
                    setReportData(prev => ({ 
                      ...prev, 
                      customQueries: [...prev.customQueries, newQuery.trim()] 
                    }));
                    setNewQuery('');
                  }
                }}
              >
                Add
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              try {
                setPdfGenerating(true);
                setReportDialogOpen(false);
                
                const reportRequest = {
                  ...reportData,
                  sessionId: sessionId || 'report-session',
                  organizationId: user.organizationId || 1
                };
                
                const pdfBlob = await generateBusinessReport(reportRequest);
                
                // Create download link
                const url = window.URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${reportData.reportTitle || 'business-report'}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                setNotification({
                  open: true,
                  message: "Report generated successfully!",
                  severity: "success"
                });
              } catch (error) {
                console.error('Report generation failed:', error);
                setNotification({
                  open: true,
                  message: "Failed to generate report. Please try again.",
                  severity: "error"
                });
              } finally {
                setPdfGenerating(false);
              }
            }}
            variant="contained"
            disabled={pdfGenerating}
            startIcon={pdfGenerating ? <CircularProgress size={20} /> : <PdfIcon />}
          >
            {pdfGenerating ? 'Generating...' : 'Generate PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DepartmentTaskChatbot;
